<script setup>
  import {computed, onBeforeMount, ref, watch} from "vue";

  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import {usePage} from "@inertiajs/vue3";
  import dosageModel from "@models/dosageModel.js";
  import MedicationComponentDetailDialogAlternativeDosageTextarea from "@pages/Records/Components/Sections/MedicationBar/Components/DetailDialog/Components/AlternativeDosageTextarea.vue";
  import MedicationComponentDetailDialogMultipleDosageInput from "@pages/Records/Components/Sections/MedicationBar/Components/DetailDialog/Components/MultipleDosageInput.vue";
  import MedicationComponentDetailDialogSingleDosageInput from "@pages/Records/Components/Sections/MedicationBar/Components/DetailDialog/Components/SingleDosageInput.vue";
  import {getAlternativeDosageMessage} from "@pages/Records/Helper/alternativeDosageMessage.js";
  import {cloneDeep} from "es-toolkit";

  defineOptions({
    name: "MedicationComponentDetailDialogAlternativeDosageData",
  });

  const props = defineProps({
    medicationForm: {
      type: Object,
      default: () => ({}),
    },
  });

  const emit = defineEmits([
    "isInvalid",
    "isValid",
    "alternativeDosageIdsChanged",
    "initType",
    "change",
    "dosageChange",
  ]);

  const page = usePage();

  const dosage = ref(null);
  const errorMessage = ref("");
  const intervalValue = ref("");
  const intervalWording = ref(null);
  const type = ref(null);
  const typeWording = ref(null);

  const show = computed(() => {
    if (type.value === "special") {
      return type.value;
    }

    if (intervalValue.value > 0 && type.value.length > 0) {
      return type.value;
    }

    return "";
  });

  const showIntervalValue = computed(() => type.value && type.value !== "special");

  watch(
    () => props.medicationForm.dosage,
    () => {
      Object.assign(dosage.value, props.medicationForm.dosage);
    },
    {deep: true},
  );

  watch(
    () => intervalValue.value || type.value,
    () => {
      checkIntervalValue();
    },
  );

  // The unit is baked into the rendered `text` (see buildIntervalString), which
  // is only rebuilt on type, interval or amount changes - so changing the
  // medication's unit alone left the special dosage showing the previous one.
  // Type "special" is skipped: its text is pure free text without a unit and
  // would be wiped by a rebuild.
  watch(
    () => props.medicationForm.dosage?.selected_unit,
    (newUnit) => {
      if (!dosage.value?.dosagealternative || !type.value || type.value === "special") {
        return;
      }

      // handleDosageChange() in the parent replaces the whole dosage object, so
      // the new unit has to be in the local copy before emitting
      dosage.value.selected_unit = newUnit;
      dosage.value.selected_unit_key_doe = props.medicationForm.dosage?.selected_unit_key_doe;

      dosage.value.dosagealternative.text = getAlternativeDosageMessage(type.value, {
        type: type.value,
        interval: intervalValue.value,
        text: null,
        data: dosage.value.dosagealternative.dosagealternativedatas ?? [],
        unit: newUnit,
      });

      emit("dosageChange", dosage.value);
    },
  );

  watch(
    () => intervalValue.value,
    () => {
      // without a type there is no alternative dosage yet (e.g. right after
      // mounting with the "Sonderdosierung" checkbox freshly checked) - writing
      // `interval`/`text` here would create a `dosagealternative` object with a
      // `type` key that was never set, which the DB requires (NOT NULL, no default)
      if (dosage.value?.dosagealternative && type.value) {
        dosage.value.dosagealternative.interval = intervalValue.value;
        dosage.value.dosagealternative.text = getAlternativeDosageMessage(type.value, {
          type: type.value,
          interval: intervalValue.value,
          text: null,
          data: dosage.value.dosagealternative.dosagealternativedatas ?? [],
          unit: dosage.value.selected_unit,
        });
        emit("dosageChange", dosage.value);
      }
    },
  );

  onBeforeMount(() => {
    dosage.value = cloneDeep(dosageModel);
    Object.assign(dosage.value, props.medicationForm.dosage);

    if (!dosage.value.dosagealternative) {
      dosage.value.dosagealternative = {};
    }

    if (dosage.value.dosagealternative.type) {
      type.value = dosage.value.dosagealternative.type;
    } else {
      type.value = null;
    }

    intervalValue.value = dosage.value.dosagealternative.interval;
  });

  const handleTypeChange = (newValue) => {
    if (type.value !== newValue) {
      emit("change", newValue);

      intervalValue.value = 0;

      if (type.value === null || type.value === 0) {
        clearDefaultDosage();
      } else if (type.value !== null && !newValue) {
        restoreDefaultDosage();
      }
    }

    // build the whole object in one assignment - never leave `type` set without
    // `interval`/`text`, since a DB row requires all three to be present together
    dosage.value.dosagealternative = newValue
      ? {
          type: newValue,
          interval: intervalValue.value,
          text: getAlternativeDosageMessage(newValue, {
            type: newValue,
            interval: intervalValue.value,
            text: null,
            data: [],
            unit: dosage.value.selected_unit,
          }),
        }
      : {};
    emit("dosageChange", dosage.value);

    type.value = newValue;
    typeWording.value = getTypeWording();
    intervalWording.value = getIntervalWording();

    emit("alternativeDosageIdsChanged");
  };

  const clearDefaultDosage = () => {
    dosage.value.morning = 0;
    dosage.value.morning_as_fraction = false;

    dosage.value.noon = 0;
    dosage.value.noon_as_fraction = false;

    dosage.value.evening = 0;
    dosage.value.evening_as_fraction = false;

    dosage.value.night = 0;
    dosage.value.night_as_fraction = false;

    emit("dosageChange", dosage.value);
  };

  const restoreDefaultDosage = () => {
    const oldData = page.props.medications?.data.find((medication) => medication.id === props.medicationForm.id);

    dosage.value.dosagealternative = {};

    dosage.value.morning = oldData.dosage.morning;
    dosage.value.morning_as_fraction = oldData.dosage.morning_as_fraction;

    dosage.value.noon = oldData.dosage.noon;
    dosage.value.noon_as_fraction = oldData.dosage.noon_as_fraction;

    dosage.value.evening = oldData.dosage.evening;
    dosage.value.evening_as_fraction = oldData.dosage.evening_as_fraction;

    dosage.value.night = oldData.dosage.night;
    dosage.value.night_as_fraction = oldData.dosage.night_as_fraction;

    emit("dosageChange", dosage.value);
  };

  const getTypeWording = () => {
    switch (type.value) {
      case "specialDays":
        return "jeden";

      case "specialWeeks":
        return "jede";

      case "multipleHours":
        return "alle";
    }

    return "";
  };

  const getIntervalWording = () => {
    switch (type.value) {
      case "multipleDay":
        return "mal / Tag";

      case "specialDays":
        return ". Tag";

      case "specialMonth":
        return "mal / Jahr";

      case "multipleWeek":
        return "mal / Woche";

      case "multipleMonth":
        return "mal / Monat";

      case "specialWeeks":
        return ". Woche";

      case "multipleHours":
        return "Stunden";
    }

    return "";
  };

  const handleChange = ({newData, text}) => {
    dosage.value.dosagealternative.type = type.value;
    dosage.value.dosagealternative.interval = intervalValue.value;
    dosage.value.dosagealternative.dosagealternativedatas = newData;

    dosage.value.dosagealternative.text = getAlternativeDosageMessage(type.value, {
      type: type.value,
      interval: intervalValue.value,
      text: text,
      data: newData,
      unit: dosage.value.selected_unit,
    });

    emit("dosageChange", dosage.value);
  };

  const checkIntervalValue = () => {
    if (showIntervalValue.value) {
      if (intervalValue.value === null || intervalValue.value === "0" || intervalValue.value === 0) {
        errorMessage.value = "Bitte tragen Sie ein gültiges Intervall ein.";
        emit("isInvalid", "checkIntervalValue");
      } else {
        errorMessage.value = "";
        emit("isValid", "checkIntervalValue");
      }
    } else {
      errorMessage.value = "";
      emit("isValid", "checkIntervalValue");
    }
  };

  defineExpose({
    restoreDefaultDosage,
  });
</script>

<template>
  <div>
    <div class="flex items-baseline space-x-2" :data-test="getTestId('alternative-container')">
      <div class="w-40">
        <ComponentSelect
          id="alternativeDosageType"
          name="alternativeDosageType"
          :model-value="type"
          placeholder="Bitte auswählen"
          key-name="name"
          :test-id="getTestId('alternative-type')"
          @change="handleTypeChange"
        >
          <template #default>
            <option value="special">Freitextdosierung</option>
            <option value="multipleDay">[...] mal / Tag</option>
            <option value="specialDays">Jeden [...]. Tag</option>
            <option value="multipleWeek">[...] mal / Woche</option>
            <option value="specialWeeks">Jede [...]. Woche</option>
            <option value="multipleMonth">[...] mal / Monat</option>
            <option value="specialMonth">[...] mal / Jahr</option>
            <option value="multipleHours">Alle [...] Stunden</option>
          </template>
        </ComponentSelect>
      </div>

      <template v-if="dosage.selected_unit !== null">
        <div v-if="showIntervalValue && typeWording" class="text-sm">
          {{ typeWording }}
        </div>

        <div v-if="showIntervalValue" class="w-8">
          <ComponentInput
            v-model="intervalValue"
            hide-clear
            :dosage-field="true"
            maxlength="2"
            :show-maxlength-count="false"
            input-alignment="text-center"
            :test-id="getTestId('alternative-interval')"
          />
        </div>

        <div
          v-if="showIntervalValue"
          class="text-sm"
          :class="{pl: intervalWording && intervalWording.charAt(0) !== '.'}"
        >
          {{ intervalWording }}
        </div>
      </template>
    </div>

    <div v-if="dosage.selected_unit === null && type !== null && type !== 'special'" class="mt-3 max-w-xl">
      <div class="mt-2 w-full text-xs text-mcred">Bitte wählen Sie zuerst oben eine Einheit aus</div>
    </div>

    <div v-else class="mt-3 max-w-xl">
      <MedicationComponentDetailDialogAlternativeDosageTextarea
        v-if="show === 'special'"
        type="special"
        data-hash-id="special"
        :dosage-form="dosage"
        :test-id="getTestId('alternative')"
        @is-invalid="$emit('isInvalid', $event)"
        @is-valid="$emit('isValid', $event)"
        @change="handleChange"
      />

      <MedicationComponentDetailDialogMultipleDosageInput
        v-if="show === 'multipleDay'"
        data-hash-id="multipleDay"
        :dosage-form="dosage"
        type="multipleDay"
        :interval-value="intervalValue"
        :test-id="getTestId('alternative')"
        @is-invalid="$emit('isInvalid', $event)"
        @is-valid="$emit('isValid', $event)"
        @change="handleChange"
      />

      <MedicationComponentDetailDialogSingleDosageInput
        v-if="show === 'specialDays'"
        data-hash-id="specialDays"
        :dosage-form="dosage"
        type="specialDays"
        :interval-value="intervalValue"
        :test-id="getTestId('alternative')"
        @is-invalid="$emit('isInvalid', $event)"
        @is-valid="$emit('isValid', $event)"
        @change="handleChange"
      />

      <MedicationComponentDetailDialogMultipleDosageInput
        v-if="show === 'multipleWeek'"
        data-hash-id="multipleWeek"
        :dosage-form="dosage"
        type="multipleWeek"
        :interval-value="intervalValue"
        :test-id="getTestId('alternative')"
        @is-invalid="$emit('isInvalid', $event)"
        @is-valid="$emit('isValid', $event)"
        @change="handleChange"
      />

      <MedicationComponentDetailDialogMultipleDosageInput
        v-if="show === 'multipleMonth'"
        data-hash-id="multipleMonth"
        :dosage-form="dosage"
        type="multipleMonth"
        :interval-value="intervalValue"
        :test-id="getTestId('alternative')"
        @is-invalid="$emit('isInvalid', $event)"
        @is-valid="$emit('isValid', $event)"
        @change="handleChange"
      />

      <MedicationComponentDetailDialogSingleDosageInput
        v-if="show === 'specialWeeks'"
        data-hash-id="specialWeeks"
        :dosage-form="dosage"
        type="specialWeeks"
        :interval-value="intervalValue"
        :test-id="getTestId('alternative')"
        @is-invalid="$emit('isInvalid', $event)"
        @is-valid="$emit('isValid', $event)"
        @change="handleChange"
      />

      <MedicationComponentDetailDialogMultipleDosageInput
        v-if="show === 'specialMonth'"
        data-hash-id="specialMonth"
        :dosage-form="dosage"
        type="specialMonth"
        :interval-value="intervalValue"
        :test-id="getTestId('alternative')"
        @is-invalid="$emit('isInvalid', $event)"
        @is-valid="$emit('isValid', $event)"
        @change="handleChange"
      />

      <MedicationComponentDetailDialogSingleDosageInput
        v-if="show === 'multipleHours'"
        data-hash-id="multipleHours"
        :dosage-form="dosage"
        type="multipleHours"
        :interval-value="intervalValue"
        :test-id="getTestId('alternative')"
        @is-invalid="$emit('isInvalid', $event)"
        @is-valid="$emit('isValid', $event)"
        @change="handleChange"
      />

      <div v-if="errorMessage" class="mt-2 w-full text-xs text-mcred" :data-test="getTestId('alternative-error')">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>
